import React, { type JSX } from "react";
import { Row, TableInstance } from "react-table";
import type { DealData } from "./dealTypes";

const SKELETON_ROWS = 10;

/** Column widths differ for the narrow actions column and the description. */
function cellClass(columnId: string): string {
  if (columnId === "actions") return "px-2 w-8";
  return columnId === "business" ? "px-3" : "px-3 truncate max-w-48";
}

function TableHead({ headerGroups }: { headerGroups: TableInstance<DealData>["headerGroups"] }) {
  return (
    <thead className="bg-cvc-tableHeader sticky top-0 z-10">
      {headerGroups.map((headerGroup) => {
        const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
        return (
          <tr key={headerGroupKey} {...headerGroupProps}>
            {headerGroup.headers.map((column) => {
              const { key: headerKey, ...headerProps } = column.getHeaderProps();
              return (
                <th
                  key={headerKey}
                  {...headerProps}
                  scope="col"
                  className={`py-3 text-left text-sm font-cvcBody font-normal text-cvc-headerText capitalize tracking-wider ${
                    column.id === "actions" ? "px-2 w-8" : "px-3"
                  }`}
                >
                  {column.render("Header") as React.ReactNode}
                </th>
              );
            })}
          </tr>
        );
      })}
    </thead>
  );
}

/** Placeholder rows shown while a search is running. */
function SkeletonRows({ columnCount }: { columnCount: number }): JSX.Element {
  return (
    <>
      {[...Array(SKELETON_ROWS)].map((_, rowIndex) => (
        <tr key={`skeleton-${rowIndex}`}>
          {[...Array(columnCount)].map((__, colIndex) => (
            <td key={`skeleton-cell-${rowIndex}-${colIndex}`} className="px-6 py-4">
              <div className="h-4 bg-gray-200 rounded-sm animate-pulse w-3/4" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** One deal; clicking anywhere on it opens the details modal. */
function DealRow({ row, onClick }: { row: Row<DealData>; onClick: () => void }): JSX.Element {
  const { key, ...rowProps } = row.getRowProps();
  return (
    <tr
      key={key}
      {...rowProps}
      className="hover:bg-cvc-lb2 transition-colors duration-200 cursor-pointer"
      onClick={onClick}
    >
      {row.cells.map((cell) => {
        const { key: cellKey, ...cellProps } = cell.getCellProps();
        return (
          <td
            key={cellKey}
            {...cellProps}
            className={`py-4 text-sm text-cvc-text ${cellClass(cell.column.id)}`}
          >
            {cell.render("Cell") as React.ReactNode}
          </td>
        );
      })}
    </tr>
  );
}

/** Body content: skeletons while loading, the deals, or the empty state. */
function TableBodyRows({
  table,
  isLoading,
  columnCount,
  onRowClick,
}: {
  table: TableInstance<DealData>;
  isLoading: boolean;
  columnCount: number;
  onRowClick: (deal: DealData) => void;
}): JSX.Element {
  if (isLoading) return <SkeletonRows columnCount={columnCount} />;

  if (table.rows.length === 0) {
    return (
      <tr key="empty-state-row">
        <td colSpan={columnCount} className="px-6 py-4 text-center text-sm text-gray-500">
          No deals to display.
        </td>
      </tr>
    );
  }

  return (
    <>
      {table.rows.map((row) => {
        table.prepareRow(row);
        return <DealRow key={row.id} row={row} onClick={() => onRowClick(row.original)} />;
      })}
    </>
  );
}

/** The scrollable deals grid. */
export default function DealsTableGrid({
  table,
  isLoading,
  columnCount,
  onRowClick,
}: {
  table: TableInstance<DealData>;
  isLoading: boolean;
  columnCount: number;
  onRowClick: (deal: DealData) => void;
}): JSX.Element {
  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 w-full min-w-0 border border-cvc-tableBorder rounded-[5px] overflow-y-auto overflow-x-auto">
        <table
          {...table.getTableProps()}
          className="min-w-full tablet:min-w-[900px] divide-y divide-cvc-tableBorder"
        >
          <TableHead headerGroups={table.headerGroups} />
          <tbody
            {...table.getTableBodyProps()}
            className="bg-white divide-y divide-cvc-tableBorder font-cvcBody"
          >
            <TableBodyRows
              table={table}
              isLoading={isLoading}
              columnCount={columnCount}
              onRowClick={onRowClick}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}
